// Types
interface ServiceCode {
  PAYMENT: string;
  TO_CARD: string;
  TO_ACCOUNT: string;
}

interface Currency {
  VND: string;
  USD: string;
}

interface VietQRFields {
  is_dynamic_qr: boolean;
  merchant_category: string;
  merchant_name: string;
  merchant_city: string;
  postal_code: string;
  currency: string;
  country_code: string;
  amount: string;
  acq: string;
  merchant_id: string;
  service_code: string;
  bill_number: string;
  mobile_number: string;
  store_label: string;
  loyalty_number: string;
  ref_label: string;
  customer_label: string;
  terminal_label: string;
  purpose_txn: string;
  additional_data: string;
  lang_ref: string;
  local_merchant_name: string;
  local_merchant_city: string;
  uuid: string;
  ipn_url: string;
  app_package_name: string;
  custom_data: string;
}

type Presence = "M" | "O" | "C";

// Constants
const SERVICE_CODE: ServiceCode = {
  PAYMENT: "QRPUSH",
  TO_CARD: "QRIBFTTC",
  TO_ACCOUNT: "QRIBFTTA",
};

const NAPAS_GUID = "A000000727";

const CURRENCY: Currency = {
  VND: "704",
  USD: "840",
};

const FIELDS: VietQRFields = {
  is_dynamic_qr: true,
  merchant_category: "",
  merchant_name: "",
  merchant_city: "",
  postal_code: "",
  currency: "704",
  country_code: "VN",
  amount: "0",
  acq: "970403",
  merchant_id: "",
  service_code: SERVICE_CODE.TO_ACCOUNT,
  bill_number: "",
  mobile_number: "",
  store_label: "",
  loyalty_number: "",
  ref_label: "",
  customer_label: "",
  terminal_label: "",
  purpose_txn: "",
  additional_data: "",
  lang_ref: "",
  local_merchant_name: "",
  local_merchant_city: "",
  uuid: "",
  ipn_url: "",
  app_package_name: "",
  custom_data: "",
};

// Utility Functions
function padLeft(n: string | number, width: number, z: string = "0"): string {
  const nStr = n.toString();
  return nStr.length >= width
    ? nStr
    : new Array(width - nStr.length + 1).join(z) + nStr;
}

// CRC Class
class CRC {
  static stringToUtf8ByteArray(str: string): number[] {
    const out: number[] = [];
    let p = 0;

    for (let i = 0; i < str.length; i++) {
      let c = str.charCodeAt(i);
      if (c < 128) {
        out[p++] = c;
      } else if (c < 2048) {
        out[p++] = (c >> 6) | 192;
        out[p++] = (c & 63) | 128;
      } else if (
        (c & 0xfc00) === 0xd800 &&
        i + 1 < str.length &&
        (str.charCodeAt(i + 1) & 0xfc00) === 0xdc00
      ) {
        // Surrogate Pair
        c = 0x10000 + ((c & 0x03ff) << 10) + (str.charCodeAt(++i) & 0x03ff);
        out[p++] = (c >> 18) | 240;
        out[p++] = ((c >> 12) & 63) | 128;
        out[p++] = ((c >> 6) & 63) | 128;
        out[p++] = (c & 63) | 128;
      } else {
        out[p++] = (c >> 12) | 224;
        out[p++] = ((c >> 6) & 63) | 128;
        out[p++] = (c & 63) | 128;
      }
    }
    return out;
  }

  /**
   * Function này phải dùng stringToUtf8ByteArray để convert về Byte[]
   */
  static getCrc16(str: string, offset: number = 0): string {
    const data = this.stringToUtf8ByteArray(str);
    if (
      data == null ||
      offset < 0 ||
      offset > data.length - 1 ||
      offset > data.length
    ) {
      return "0";
    }

    let crc = 0xffff;
    for (let i = 0; i < str.length; ++i) {
      crc ^= data[offset + i] << 8;
      for (let j = 0; j < 8; ++j) {
        crc = (crc & 0x8000) > 0 ? (crc << 1) ^ 0x1021 : crc << 1;
      }
    }
    return (crc & 0xffff).toString(16).toUpperCase();
  }

  /**
   * Function này không dùng stringToUtf8ByteArray để convert về Byte[]
   */
  static getCrc16Array(
    text: string | string[][],
    hexOutput: boolean = true
  ): string {
    let textArray: string[][];
    if (!Array.isArray(text)) {
      textArray = [[text as string]];
    } else {
      textArray = text;
    }

    const polynomial = 0x1021;
    const result = textArray.map((row) =>
      row.map((string) => {
        if (!string.length) return null;
        const bytes = Array.from(String(string)).map(
          (char) => char.charCodeAt(0) & 0xff
        );
        let crc = 0xffff;
        bytes.forEach((byte) => {
          for (let i = 0; i < 8; i++) {
            const bit = 1 === ((byte >> (7 - i)) & 1);
            const c15 = 1 === ((crc >> 15) & 1);
            crc <<= 1;
            if (c15 !== bit) crc ^= polynomial;
          }
        });
        crc &= 0xffff;

        let crcHexString = crc.toString(16).toUpperCase();
        crcHexString =
          crcHexString.length < 4 ? padLeft(crcHexString, 4) : crcHexString;
        return hexOutput ? crcHexString : crc;
      })
    );
    return result.toString();
  }

  /**
   * This function is used for replace special character and Vietnamese Utf-8 character to ASCII character
   */
  static nonAccentVietnamese(str: string): string {
    let result = str.toLowerCase();
    result = result.replace(new RegExp("/", "g"), "-");
    result = result.replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, "a");
    result = result.replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, "e");
    result = result.replace(/ì|í|ị|ỉ|ĩ/g, "i");
    result = result.replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, "o");
    result = result.replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, "u");
    result = result.replace(/ỳ|ý|ỵ|ỷ|ỹ/g, "y");
    result = result.replace(/đ/g, "d");
    // Some system encode vietnamese combining accent as individual utf-8 characters
    result = result.replace(/\u0300|\u0301|\u0303|\u0309|\u0323/g, ""); // Huyền sắc hỏi ngã nặng
    result = result.replace(/\u02C6|\u0306|\u031B/g, ""); // Â, Ê, Ă, Ơ, Ư
    return result.toUpperCase().trim();
  }
}

// TLV Class
class TLV {
  tagId: number;
  tagName: string;
  tagLength: number;
  tagValue: string | TLV[];
  presence: Presence;

  constructor(
    id: number = 0,
    name: string = "",
    length: number = 99,
    isFixed: boolean = true,
    presence: Presence = "O",
    value: string | TLV[] = ""
  ) {
    this.tagId = id;
    this.tagName = name;
    if (isFixed) {
      this.tagLength = length;
    } else {
      this.tagLength = name.length;
    }
    this.tagValue = value;
    this.presence = presence;
  }

  toString(): string {
    let value = "";
    if (Array.isArray(this.tagValue)) {
      for (let de = 0; de < this.tagValue.length; de++) {
        if (this.tagValue[de] instanceof TLV) {
          if (this.tagValue[de].tagValue !== "") {
            value += this.tagValue[de].toString();
          }
        }
      }
    } else {
      value = this.tagValue;
    }

    if (value === "") {
      return "";
    } else {
      this.tagLength = value.length;
      return `${this.tagId >= 10 ? `${this.tagId}` : `0${this.tagId}`}${
        this.tagLength >= 10 ? `${this.tagLength}` : `0${this.tagLength}`
      }${value}`;
    }
  }
}

// Main VIETQR Class
class VIETQR {
  data: (TLV | undefined)[];
  fields: VietQRFields;

  constructor() {
    this.data = [];
    this.fields = { ...FIELDS };
  }

  toStringNoCrc(): string {
    let str = "";
    for (let de = 0; de < this.data.length; de++) {
      if (this.data[de] instanceof TLV) {
        const deString = this.data[de]!.toString();
        str += deString;
      }
    }
    return `${str}6304`;
  }

  getCrc(): string {
    return CRC.getCrc16Array(this.toStringNoCrc());
  }

  toString(): string {
    const semiVietqr = this.toStringNoCrc();
    const crcValue = this.getCrc();
    return `${semiVietqr}${crcValue}`;
  }

  builder(): string {
    this.data[0] = new TLV(0, "Payload Format Indicator", 2, true, "M", "01");
    this.data[1] = new TLV(
      1,
      "QR Type",
      2,
      true,
      "M",
      this.fields.is_dynamic_qr ? "12" : "11"
    );
    this.data[38] = new TLV(
      38,
      "QR code service on NAPAS system",
      99,
      false,
      "M",
      [
        new TLV(
          0,
          "Global Unique Identifier - GUID",
          10,
          true,
          "M",
          NAPAS_GUID
        ),
        new TLV(1, "Payment network specific", 32, false, "M", [
          new TLV(0, "Acquier ID/BNB ID", 6, true, "M", this.fields.acq),
          new TLV(
            1,
            "Merchant ID/Consumer ID",
            19,
            false,
            "M",
            this.fields.merchant_id
          ),
        ]),
        new TLV(2, "Service Code", 10, false, "C", this.fields.service_code),
      ]
    );
    this.data[52] = new TLV(
      52,
      "Merchant Category Code",
      4,
      true,
      "O",
      this.fields.merchant_category
    );
    this.data[53] = new TLV(
      53,
      "Transaction Currency",
      3,
      true,
      "M",
      this.fields.currency
    );
    this.data[54] = new TLV(
      54,
      "Transaction Amount",
      13,
      false,
      "C",
      this.fields.is_dynamic_qr ? this.fields.amount : ""
    );
    this.data[55] = new TLV(
      55,
      "Tip or Convenience Indicator",
      2,
      true,
      "O",
      ""
    );
    this.data[56] = new TLV(
      56,
      "Value of Convenience Fee Fixed",
      13,
      false,
      "O",
      ""
    );
    this.data[57] = new TLV(
      57,
      "Value of Convenience Fee Percentage",
      5,
      false,
      "O",
      ""
    );
    this.data[58] = new TLV(
      58,
      "Country Code",
      2,
      true,
      "M",
      this.fields.country_code
    );
    this.data[59] = new TLV(
      59,
      "Merchant Name",
      25,
      false,
      "O",
      this.fields.merchant_name
    );
    this.data[60] = new TLV(
      60,
      "Merchant City",
      15,
      false,
      "O",
      this.fields.merchant_city
    );
    this.data[61] = new TLV(
      61,
      "Postal Code",
      10,
      false,
      "O",
      this.fields.postal_code
    );
    this.data[62] = new TLV(
      62,
      "Additional Data Field Template",
      99,
      true,
      "O",
      [
        new TLV(0, "", 0, true, "O", ""), // null placeholder
        new TLV(1, "Bill Number", 25, false, "C", this.fields.bill_number),
        new TLV(2, "Mobile Number", 25, false, "C", this.fields.mobile_number),
        new TLV(3, "Store Label", 25, false, "O", this.fields.store_label),
        new TLV(
          4,
          "Loyalty Number",
          25,
          false,
          "O",
          this.fields.loyalty_number
        ),
        new TLV(5, "Reference Label", 25, false, "C", this.fields.ref_label),
        new TLV(
          6,
          "Customer Label",
          25,
          false,
          "C",
          this.fields.customer_label
        ),
        new TLV(
          7,
          "Terminal Label",
          25,
          false,
          "O",
          this.fields.terminal_label
        ),
        new TLV(
          8,
          "Purpose of Transaction",
          25,
          false,
          "C",
          CRC.nonAccentVietnamese(this.fields.purpose_txn)
        ),
        new TLV(
          9,
          "Additional Consumer Data Request",
          3,
          false,
          "O",
          this.fields.additional_data
        ),
      ]
    );
    this.data[63] = new TLV(
      63,
      "CRC (Cyclic Redundancy Check)",
      4,
      true,
      "M",
      ""
    );
    this.data[64] = new TLV(
      64,
      "Merchant Information - Language Template",
      2,
      true,
      "O",
      [
        new TLV(0, "Language Preference", 2, true, "M", this.fields.lang_ref),
        new TLV(
          1,
          "Merchant Name - Alternate Language",
          25,
          false,
          "M",
          this.fields.local_merchant_name
        ),
        new TLV(
          2,
          "Merchant City - Alternate Language",
          15,
          false,
          "O",
          this.fields.local_merchant_city
        ),
      ]
    );
    this.data[80] = new TLV(80, "UniCube data", 99, false, "O", [
      new TLV(0, "uuid", 16, false, "O", this.fields.uuid),
      new TLV(1, "custom data", 83, false, "O", this.fields.custom_data),
    ]);

    return this.toString();
  }
}

// Exports
export { VIETQR, SERVICE_CODE, CURRENCY };
export type { VietQRFields, ServiceCode, Currency };
